<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import { VButton, VIcon } from '@/modules/shared/components'
import { useSkeleton } from '@/modules/core/composables/useSkeleton'
import { scrollToTop } from '@/modules/investing/utils/scroll-to-section'
import IndividualAccountLayout from '@/modules/investing/components/individuals/individual-account-layout.vue'
import { useWorkspaceStore } from '@/modules/workspace/stores/workspace-store'
import { useAuthStore } from '@/modules/auth/stores/auth-store'
import { useAccreditationStore } from '@/modules/screening/stores/accreditation-store'
import IndividualLayout from '@/modules/investing/components/individuals/individual-layout.vue'
import { useIndividualStore } from '@/modules/investing/stores/better-individual-store'
import AccreditationForm from '@/modules/screening/component/AccreditationForm.vue'

const route = useRoute()
const { individual_id, slug } = route.params as { individual_id: string; slug: string }

const workspaceStore = useWorkspaceStore()
const accreditationStore = useAccreditationStore()
const individualStore = useIndividualStore()
const currentIndividual = computed(() => individualStore.individual)
const currentAccreditation = computed(() => accreditationStore.screeningAccreditation)
const accreditationForm = ref(null)
const inFormMode = ref(false)
const isLoading = ref(false)

const { skeleton, hideSkeleton, showSkeleton } = useSkeleton()

const fetch = async () => {
  showSkeleton()
  await individualStore.fetchIndividual(individual_id, { slug })
  await accreditationStore.getAccreditation(currentIndividual.value.screening_accreditation_id, { slug })
  hideSkeleton()
}

const editForm = () => {
  inFormMode.value = true
}

const cancelEdit = () => {
  accreditationForm.value.resetForm()
  inFormMode.value = false
  scrollToTop()
}

const submit = async (payload) => {
  isLoading.value = true
  await accreditationStore.updateResponse(currentAccreditation.value?.id, payload, { slug })
  cancelEdit()
  isLoading.value = false
  await fetch()
}

const confirmChanges = async (payload) => {
  await accreditationForm.value.submit()
}

///////////////////////////////////////////////////////////////////////////////
// Authorization
///////////////////////////////////////////////////////////////////////////////

const authStore = useAuthStore()
const is_viewing_own_profile = computed(() => authStore.current_user?.investor_id?.toString() === individual_id)

onMounted(async () => {
  await workspaceStore.fetchCurrent()
  await fetch()
})
</script>

<template>
  <IndividualLayout selectedTab="account">
    <IndividualAccountLayout selectedTab="accreditation">
      <AccreditationForm
        ref="accreditationForm"
        :accreditation="currentAccreditation"
        :inDisplayMode="!inFormMode"
        :skeleton="skeleton"
        :onSubmit="submit"
      >
        <template #actions v-if="is_viewing_own_profile">
          <VButton variant="v-white" :click="editForm" v-if="!inFormMode">
            <div class="flex items-center gap-2 stroke-[#3b88af] text-sm">
              Edit
              <VIcon name="Pencil" />
            </div>
          </VButton>
          <VButton variant="v-blue" size="lg" :click="confirmChanges" :loading="isLoading" v-if="inFormMode"
            >Confirm Changes</VButton
          >
          <VButton variant="v-white" :click="cancelEdit" v-if="inFormMode">
            <div class="flex items-center gap-2 stroke-[#3b88af] text-sm">
              Cancel
              <VIcon name="X" />
            </div>
          </VButton>
        </template>
      </AccreditationForm>
    </IndividualAccountLayout>
  </IndividualLayout>
</template>
